import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { Prompt, StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import categoriesStore from '../../store/categoriesStore';
import { defaultIntegrationTooltipOptions, RESTRICTION_ACCESS } from 'src/constants/integrations';
import { SCREENS } from 'src/constants/screens';
import integrationStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { getTranslate, isDisabledByPos, stringTransform } from 'src/utils/helpers';
import { numberValidation } from 'src/utils/validation';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import MultiLanguageTextArea from 'src/view/components/Controls/MultiLanguageTextArea/MultiLanguageTextArea';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import { getPromptProps } from 'src/view/components/CustomPrompt/helper';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import IntegrationTooltip from 'src/view/components/IntegrationTooltip/IntegrationTooltip';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import Text, { TEXT } from 'src/view/components/Text/Text';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { BUTTON_TITLE } from '~/constants/main';
import './style.scss';

type TProps = RouteComponentProps<{ id: string }, StaticContext, undefined>;

const CategoriesEdit: React.FC<TProps> = observer((props: TProps): JSX.Element => {
  useEffect(() => {
    if (!categoriesStore.categoriesCount) categoriesStore.fetchCategoriesCount();

    if (props.match.params.id) {
      categoriesStore.setEditableCategory(categoriesStore.pagination.data.find((category) => category.id === Number(props.match.params.id)));
      if (!categoriesStore.editableCategory?.id) categoriesStore.getCategory(props.match.params.id);
      return;
    }

    categoriesStore.setEditableCategory();
  }, []);

  const clickSave = () => {
    const { editableCategory, createCategory, updateCategory, setValidation } = categoriesStore;
    if (!editableCategory) return;
    setValidation();

    if (!categoriesStore.validation.valid) return;

    const categoryId = Number(props.match.params.id);
    if (categoryId) {
      updateCategory(categoryId);
    } else {
      createCategory();
    }
  };

  const onSortNumberChange = useCallback((value: string) => {
    if (!numberValidation(value)) return;
    categoriesStore.changeCategorySort(stringTransform(value));
  }, []);

  const {
    categoryName: nameRestrictionAccess,
    categoryDescription: descRestrictionAccess,
    categoryAvailability: availabilityRestrictionAccess,
  } = integrationStore.posRestrictions || {};

  const isEditing: boolean = !!props.match.params.id;

  return (
    <>
      <Headers>
        <HeaderBack
          title={categoriesStore.editableCategory ? getTranslate(categoriesStore.editableCategory.name) : ''}
          noTranslation
          path={SCREENS.CATEGORIES()}
        />
      </Headers>
      <ScrollableBlock showScrollbar className="full-height">
        {categoriesStore.editableCategory ? (
          <div className="d-flex flex-column w-fit-content mt-3">
            <div className="d-flex flex-row">
              <div className="d-flex flex-column mt-3 mr-3">
                <IntegrationManager setting={isEditing ? nameRestrictionAccess : RESTRICTION_ACCESS.ALLOWED} wrapperClassName="d-flex flex-column">
                  <Text type={TEXT.BLOCK_HEADER} text="category_name" disabled={isDisabledByPos(nameRestrictionAccess, !isEditing)} required />
                  <MultiLanguageInput
                    maxLength={75}
                    enText={categoriesStore.editableCategory.name.en}
                    arText={categoriesStore.editableCategory.name.ar}
                    validation={categoriesStore.validation.name}
                    tooltip={
                      isDisabledByPos(nameRestrictionAccess, !isEditing)
                        ? {
                            ...defaultIntegrationTooltipOptions,
                            wrapperClassName: 'full-height',
                          }
                        : null
                    }
                    disabled={isDisabledByPos(nameRestrictionAccess, !isEditing)}
                    onChangeValue={categoriesStore.changeCategoryName}
                    className="mt-2"
                  />
                </IntegrationManager>
                <div className="d-flex flex-column mt-5">
                  <Text type={TEXT.BLOCK_HEADER} text="sort" />
                  <InputAlt
                    value={categoriesStore.editableCategory.sort}
                    className="menu-manag-tab-item-main-details__input mt-2"
                    onChangeValue={onSortNumberChange}
                  />
                </div>
              </div>
              <IntegrationManager setting={isEditing ? descRestrictionAccess : RESTRICTION_ACCESS.ALLOWED}>
                <MultiLanguageTextArea
                  title={localeStore.t('description')}
                  enText={categoriesStore.editableCategory.meta_description?.en}
                  arText={categoriesStore.editableCategory.meta_description?.ar}
                  onChangeValue={categoriesStore.changeCategoryMetaDescription}
                  tooltip={
                    isDisabledByPos(descRestrictionAccess, !isEditing)
                      ? { ...defaultIntegrationTooltipOptions, contentClassName: 'full-height' }
                      : null
                  }
                  disabled={isDisabledByPos(descRestrictionAccess, !isEditing)}
                  className="categories-edit__textarea"
                />
              </IntegrationManager>
            </div>

            <div className="d-flex flex-column mt-5">
              <p className="family-bold text-size-18 color-dark">{localeStore.t('external_id')}</p>
              <InputAlt
                value={categoriesStore.editableCategory.external_id}
                className="menu-manag-tab-item-main-details__input mt-2"
                onChangeValue={categoriesStore.changeCategoryExternalId}
              />
            </div>

            <IntegrationManager
              setting={isEditing ? availabilityRestrictionAccess : RESTRICTION_ACCESS.ALLOWED}
              wrapperClassName="d-flex flex-row align-items-center mt-4">
              <IntegrationTooltip isVisible={isDisabledByPos(availabilityRestrictionAccess, !isEditing)}>
                <Switcher status={categoriesStore.editableCategory?.status} onChange={categoriesStore.changeCategoryAvailability} />
              </IntegrationTooltip>
              <Text type={TEXT.SWITCHER_LABEL} text="availability" disabled={isDisabledByPos(availabilityRestrictionAccess, !isEditing)} />
            </IntegrationManager>
            <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={clickSave} loading={categoriesStore.isLoading} className="mt-8" />
          </div>
        ) : null}
      </ScrollableBlock>
      <LoadingOverlay visible={categoriesStore.isLoading} />
      <Prompt when={categoriesStore.edited} message={getPromptProps()} />
    </>
  );
});

export default CategoriesEdit;
